import { PageType } from '@app-types/PageType'
import SectionWrapper from '@components/common/SectionWrapper'
import LoadingSpinner from '@components/shared/Loading/Loading'
import Tooltip from '@components/shared/Tooltip/Tooltip'
import { useUserData } from '@contexts/UserDataContext'
import classNames from 'classnames'
import dayjs, { Dayjs } from 'dayjs'
import {
  buildHayirZinciriWidgetStats,
  syncNativeWidgetData,
} from 'helpers/nativeWidgetSync'
import { getNameByPageType } from 'helpers/userData'
import Link from 'next/link'
import { useCallback, useEffect, useMemo } from 'react'
import { FaChartLine, FaQuestionCircle } from 'react-icons/fa'
import AddToStatsModal from './AddToStatsModal'
import StatsStreakStatus from './StatsStreakStatus'

const getDaysBetweenDates = (start: Dayjs, end: Dayjs): string[] => {
  const dates = []
  let currentDate = start
  while (currentDate <= end) {
    dates.push(currentDate.format('DD-MM-YYYY'))
    currentDate = currentDate.add(1, 'day')
  }
  return dates
}

export type DailyStatType = {
  score: number
  [key: string]: number
}
export type DailyStatsType = {
  [key: string]: DailyStatType
}

const defaultEmptyStat: DailyStatType = {
  score: 0,
}

const getDailyStatsForLastTwoMonth = (data: DailyStatsType | undefined) => {
  const days = getDaysBetweenDates(dayjs().subtract(64, 'day'), dayjs())
  const dailyStats: DailyStatsType = {}
  days.forEach((day) => {
    dailyStats[day] = data?.[day] || defaultEmptyStat
  })

  dailyStats[dayjs().format('DD-MM-YYYY')] =
    data?.[dayjs().format('DD-MM-YYYY')] || defaultEmptyStat

  return dailyStats
}

// Find one-day gap days in the streak
const findOneGapDays = (dailyStats: DailyStatsType): string[] => {
  const gapDays: string[] = []
  const sortedDays = Object.keys(dailyStats).sort((a, b) => {
    return dayjs(a, 'DD-MM-YYYY').diff(dayjs(b, 'DD-MM-YYYY'))
  })

  for (let i = 1; i < sortedDays.length - 1; i++) {
    const prevDay = sortedDays[i - 1]
    const currentDay = sortedDays[i]
    const nextDay = sortedDays[i + 1]

    // Check if current day has no activity but both adjacent days do
    if (
      dailyStats[currentDay].score === 0 &&
      dailyStats[prevDay].score > 0 &&
      dailyStats[nextDay].score > 0
    ) {
      gapDays.push(currentDay)
    }
  }

  return gapDays
}

// return the shade of green based on the score that is based on min and max
const getColorClassForCountBasedOnMaxMin = (count: number, max: number, min: number) => {
  const percentage = (count - min) / (max - min)
  if (count === 0) {
    return 'bg-gray-300'
  }

  if (percentage < 0.2) {
    return 'bg-green-200'
  }
  if (percentage < 0.3) {
    return 'bg-green-300/75'
  }
  if (percentage < 0.4) {
    return 'bg-green-300'
  }
  if (percentage < 0.5) {
    return 'bg-green-400/75'
  }
  if (percentage < 0.6) {
    return 'bg-green-400'
  }
  if (percentage < 0.7) {
    return 'bg-green-500/75'
  }
  if (percentage < 0.8) {
    return 'bg-green-500'
  }

  return 'bg-green-600/90'
}

const Stat = ({
  count,
  day,
  max,
  min,
  data,
  isGapDay,
}: {
  count: number
  day: string
  max: number
  min: number
  data: Record<string, number>
  isGapDay?: boolean
}) => {
  const getContentEl = useCallback(
    () => (
      <div className="min-w-[140px] p-1">
        <div className="flex items-center justify-between font-bold mb-2 border-b border-main pb-2 text-brand-secondary">
          <span>{day}/</span>
          <span>{count} puan</span>
        </div>

        {isGapDay && (
          <div className="mb-2 text-[10px] font-bold text-center py-1 px-2 bg-warning-subtle text-warning rounded-full border border-warning/10">
            Aktivite Zinciri Boşluğu
          </div>
        )}

        <div className="space-y-1.5">
          {Object.keys(data).length > 0 ? (
            Object.keys(data).map((key) => (
              <div
                key={key}
                className="flex justify-between items-center gap-4 text-[11px]"
              >
                <span className="text-muted">{getNameByPageType(key as PageType)}</span>
                <span className="font-semibold text-main">{data[key]}</span>
              </div>
            ))
          ) : (
            <div className="text-center py-1 text-muted text-[10px] italic">
              Aktivite bulunamadı
            </div>
          )}
        </div>
      </div>
    ),
    [count, isGapDay, day, data],
  )

  return (
    <Tooltip content={getContentEl()}>
      <span
        className={classNames(
          'block w-full aspect-square rounded-xs hover:opacity-50 transition-all cursor-crosshair',
          isGapDay
            ? 'border border-amber-500 bg-amber-200'
            : getColorClassForCountBasedOnMaxMin(count, max, min),
        )}
      ></span>
    </Tooltip>
  )
}

const StatsInfo = (
  <div className="max-w-xs">
    <h3 className="font-medium text-brand-secondary text-base md:text-lg mb-3">
      Hayır Zinciri Hakkında
    </h3>
    <div className="text-sm space-y-3 text-main">
      <p>
        Son 2 ay içinde yapılan aktiviteler burada gösterilmektedir. Aktivite sayısına
        göre renk yoğunluğu değişmektedir.
      </p>
      <p>
        Sağ taraftaki <strong>+</strong> ikonuna tıklayarak başka yerlerde yaptığınız
        okumaları istatistiklere ekleyebilirsiniz.
      </p>
      <p>
        <strong>Hesabım</strong> sayfasında detaylı istatistikleri görebilirsiniz ve
        istatistiklerinizi sıfırlayabilirsiniz.
      </p>
    </div>
  </div>
)

const Stats = () => {
  const { userData, loading } = useUserData()

  const dailyStats = useMemo(
    () => getDailyStatsForLastTwoMonth(userData?.stats),
    [userData?.stats],
  )
  const { max, min } = useMemo(() => getMaxMinScores(dailyStats), [dailyStats])

  const gapDays = useMemo(() => findOneGapDays(dailyStats), [dailyStats])
  const widgetStats = useMemo(
    () => buildHayirZinciriWidgetStats(dailyStats),
    [dailyStats],
  )

  const dailyStatsKeys = Object.keys(dailyStats)

  // Keep the Android home screen widget's Hayır Zinciri summary in sync.
  useEffect(() => {
    if (!loading) {
      syncNativeWidgetData({ stats: widgetStats })
    }
  }, [loading, widgetStats])

  return (
    <SectionWrapper>
      <div className="mx-auto">
        <div className="flex justify-between items-end">
          <div className="flex items-center">
            <h3 className="font-medium text-base md:text-lg">Hayır Zinciri</h3>
            <Tooltip trigger="click" content={StatsInfo}>
              <button
                className="ml-2 text-muted hover:text-brand-secondary transition-colors cursor-pointer"
                aria-label="İstatistik Bilgisi"
                title="İstatistik Bilgisi"
              >
                <FaQuestionCircle className="w-4 h-4" />
              </button>
            </Tooltip>
          </div>
          <div className="flex items-center gap-3">
            <Link
              href={'/hesabim?tab=stats'}
              className="text-muted hover:text-brand-secondary transition-colors"
              aria-label="İstatistikleri Görüntüle"
              title="İstatistikleri Görüntüle"
            >
              <FaChartLine className="w-5 h-5" />
            </Link>
            <AddToStatsModal />
          </div>
        </div>

        {loading ? (
          <LoadingSpinner fullScreen={false} />
        ) : (
          <>
            <StatsStreakStatus dailyStats={dailyStats} />
            <div className="grid grid-cols-13 gap-1.5 m-auto mt-2 max-w-[312px] w-full text-center overflow-hidden p-1">
              {dailyStatsKeys.map((day, i) => {
                const { score, ...data } = dailyStats[day] || {}
                return (
                  <Stat
                    max={max}
                    min={min}
                    key={i}
                    day={day}
                    count={score}
                    data={{ ...data }}
                    isGapDay={gapDays.includes(day)}
                  />
                )
              })}
            </div>
          </>
        )}
      </div>
    </SectionWrapper>
  )
}

export default Stats

// get max and min scores
// min should be other than 0
function getMaxMinScores(dailyStats: DailyStatsType): { max: any; min: any } {
  const scores = Object.values(dailyStats)
    .map((stat) => Boolean(stat.score) && stat.score)
    .filter(Boolean) as number[]
  const max = Math.max(...scores)
  const min = Math.min(...scores)
  return { max, min }
}
